import { ref } from 'vue';
import type { AxiosError } from 'axios';
import * as Sentry from '@sentry/vue';
import { useApi } from '@/composables/useApi.ts';
import { useMessageStore } from '@/stores/message.ts';
import i18n from '@/plugins/i18n';

// DeepValidator answers with an RFC-7807 problem detail. On 400 it may carry a
// `maxSizeBytes` hint, which is not part of the typed ProblemDetail.
interface ValidationProblemDetail {
  maxSizeBytes?: number;
}

/** Which of the two round trips failed, so the snackbar can say so. */
type ValidationStage = 'download-url' | 'validation';

function toMegabytes(bytes: number) {
  return Math.floor(bytes / (1024 * 1024));
}

export function useDeepValidator() {
  const { deepValidatorAPI } = useApi();
  const messageStore = useMessageStore();

  const validationPending = ref(false);

  function showError(error: unknown, stage: ValidationStage) {
    // The document never reached DeepValidator, so report it as what it is:
    // the file could not be retrieved.
    if (stage === 'download-url') {
      messageStore.showMessage({
        key: 'errors.api.download_document',
        color: 'error',
        icon: 'fa fa-circle-exclamation'
      });
      return;
    }

    const response = (error as AxiosError<ValidationProblemDetail>)?.response;
    const maxSizeBytes = response?.data?.maxSizeBytes;

    if (response?.status === 400 && maxSizeBytes) {
      messageStore.showMessage({
        text: i18n.global.t('errors.api.validate_signatures_file_too_large', {
          size: toMegabytes(maxSizeBytes)
        }),
        color: 'error',
        icon: 'fa fa-circle-exclamation'
      });
      return;
    }

    messageStore.showMessage({
      key:
        response?.status === 400
          ? 'errors.api.validate_signatures_unsupported_document'
          : 'errors.api.validate_signatures',
      color: 'error',
      icon: 'fa fa-circle-exclamation'
    });
  }

  /**
   * Hands a document download URL to DeepValidator and opens the resulting
   * validation report in a new tab.
   *
   * Takes a resolver rather than a URL because those URLs expire quickly: it is
   * called here, on click, so the pending flag and the error handling cover
   * fetching the URL as well as the validation itself.
   *
   * The tab is opened empty up front, still inside the click handler, because
   * popup blockers drop a `window.open` that only happens once the request has
   * resolved. It is closed again if the report never materialises.
   */
  async function validateSignatures(
    resolveDownloadUrl: () => Promise<string | undefined>
  ) {
    if (validationPending.value) return;

    validationPending.value = true;
    const validationTab = window.open('', '_blank');
    let stage: ValidationStage = 'download-url';

    try {
      const downloadUrl = await resolveDownloadUrl();

      if (!downloadUrl) {
        throw new Error('No download URL to validate signatures for');
      }

      stage = 'validation';

      const { data } =
        await deepValidatorAPI.value.cachesSignatureValidationResourceLocations.create(
          { url: downloadUrl }
        );

      if (!data.frontendUrl) {
        throw new Error('DeepValidator returned no frontend URL');
      }

      if (validationTab) {
        validationTab.location.href = data.frontendUrl;
      } else {
        // Blocked up front — a direct open may still be allowed.
        window.open(data.frontendUrl, '_blank');
      }
    } catch (error) {
      validationTab?.close();
      showError(error, stage);
      Sentry.captureException(error);
    } finally {
      validationPending.value = false;
    }
  }

  return {
    validationPending,
    validateSignatures
  };
}
